import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { makeApi, URLS } from "Utils/apiURL";
import { MP_EVENT } from "Static/constant";
import userDetail from "Stores/userDetail";
import Utils from "Utils/utils";
import { scheduleDataStore } from "Stores/ScheduleData";
import { toasterState } from "Components/common/toaster";
import SendingWindow from "./SendingWindow";
import FormActionButton from "../ConnectInbox/Configure/FormActionButton";
import TimezoneSelectDropdown from "./TimezoneSelectDropdown";

const ALL_WEEK = ["1", "2", "3", "4", "5", "6", "7"];
const QUICK_WINDOWS = [
  {
    id: "weekdays9to5",
    name: "09:00 AM to 05:00 PM (Mon - Fri)",
    days: ["1", "2", "3", "4", "5"],
    fromTime: "09:00",
    toTime: "17:00"
  },
  {
    id: "fullWeek9to5",
    name: "09:00 AM to 05:00 PM (Mon - Sun)",
    days: ALL_WEEK,
    fromTime: "09:00",
    toTime: "17:00"
  },
  {
    id: "anytime",
    name: "12:00 AM to 11:59 PM (Mon - Sun)",
    days: ALL_WEEK,
    fromTime: "00:00",
    toTime: "23:59"
  }
];

const buildQuickWindow = (preset = {}) => {
  const timeRange = {};
  (preset?.days || []).forEach(day => {
    timeRange[day] = [
      { fromTime: preset?.fromTime, toTime: preset?.toTime }
    ];
  });
  return timeRange;
};

const NameField = props => {
  const {
    scheduleNameChange = () => {},
    scheduleNameErr = "",
    scheduleObj = {}
  } = props;
  return (
    <>
      <label className="labelText">
        Name <span className="required">*</span>
      </label>
      <input
        id="scheduleName"
        name="scheduleName"
        className="scheduleName"
        type="text"
        defaultValue={scheduleObj?.name}
        onChange={e => scheduleNameChange(e.currentTarget.value)}
        autoFocus
        required
      />
      <span className="err-msg-text">{scheduleNameErr}</span>
    </>
  );
};

const CreateOrEditSchedules = observer(props => {
  const { hideCbk = () => {}, item = {}, isFromEdit = false } = props || {};
  const {
    name = "",
    timezone = "",
    timezoneName = "",
    id = "",
    dailyTimeRange = {}
  } = item || {};
  // Default a NEW schedule to the user's own timezone, not the first list
  // entry (which is GMT-11 Niue in the backend's offset ordering).
  const getDefaultTimezone = () => {
    const zones = userDetail?.globalTimeZone || [];
    let browserTz = "";
    try {
      browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    } catch (e) {
      browserTz = "";
    }
    const city = browserTz.split("/").pop()?.replace(/_/g, " ")?.toLowerCase();
    return (
      zones.find(zone => zone?.name === browserTz) ||
      (city &&
        zones.find(zone =>
          `${zone?.name} ${zone?.displayName}`.toLowerCase().includes(city)
        )) ||
      zones?.[0] ||
      {}
    );
  };

  const defaultZone = getDefaultTimezone();
  const [scheduleObj, setScheduleObj] = useState({
    name,
    timezone: timezone || defaultZone?.name,
    timezoneName: timezoneName || defaultZone?.displayName,
    timerange: dailyTimeRange
  });

  useEffect(() => {
    if (!scheduleObj?.timezone && userDetail?.globalTimeZone?.length) {
      const zone = getDefaultTimezone();
      setScheduleObj(prev => ({
        ...prev,
        timezone: prev?.timezone || zone?.name,
        timezoneName: prev?.timezoneName || zone?.displayName
      }));
    }
  }, [userDetail?.globalTimeZone]);
  const [scheduleNameErr, setScheduleNameErr] = useState(false);
  const [loader, setLoader] = useState(false);

  const scheduleNameChange = value => {
    setScheduleNameErr(value === "" ? "Please enter a valid name." : "");
    setScheduleObj({ ...scheduleObj, name: value });
  };

  const selectedTimezoneCbk = (key, value, uniqueId) => {
    setScheduleObj({
      ...scheduleObj,
      timezone: value,
      timezoneName: uniqueId
    });
  };

  const scheduleSendingWindowCbk = value => {
    setScheduleObj({ ...scheduleObj, timerange: value });
  };

  const isQuickWindowActive = (preset = {}) => {
    const current = scheduleObj?.timerange || {};
    const target = buildQuickWindow(preset);
    const currentDays = Object.keys(current).sort();
    const targetDays = Object.keys(target).sort();
    if (currentDays.join() !== targetDays.join()) {
      return false;
    }
    return currentDays.every(
      day =>
        current[day]?.length === 1 &&
        current[day][0]?.fromTime === target[day][0]?.fromTime &&
        current[day][0]?.toTime === target[day][0]?.toTime
    );
  };

  const handleQuickWindowClick = (preset = {}) => {
    scheduleSendingWindowCbk(buildQuickWindow(preset));
  };

  const handleCancelAction = () => {
    hideCbk();
  };

  const checkForValidValues = () => {
    const validTimeRange = Object.keys(scheduleObj?.timerange)?.length;
    let isValidValues = false;
    if (scheduleObj?.name === "") {
      setScheduleNameErr("Please enter a valid name.");
    } else if (scheduleObj?.name && !validTimeRange) {
      setScheduleNameErr("");
      toasterState.setToastMsg(
        "Please specify atleast 1 sending window",
        "fail"
      );
    } else {
      isValidValues = true;
    }
    return isValidValues;
  };

  const handleSaveChangesAction = async () => {
    const isValidValues = checkForValidValues();
    if (isValidValues) {
      setScheduleNameErr("");
      setLoader(true);
      const config = {
        url: isFromEdit
          ? `${URLS.saveScheduleTimeWindow}/${id}`
          : URLS.saveScheduleTimeWindow,
        method: "POST",
        data: { ...scheduleObj }
      };
      let response = await makeApi(config);
      if (response?.status === 200) {
        setLoader(false);
        toasterState.setToastMsg(
          `Successfully ${
            isFromEdit ? "updated" : "created"
          } schedules with time window`,
          "success"
        );
        Utils.mixpanelTrack(MP_EVENT.ST_SCHEDULE_SAVED, {
          pageType: "Schedule Page"
        });
        hideCbk();
        scheduleDataStore.setScheduleDataLoading(true);
        scheduleDataStore.getScheduleData();
      } else {
        toasterState.setToastMsg(
          "Something went wrong. Please try again later",
          "fail"
        );
      }
    }
  };

  const timeZoneObj = {
    name: "timeZone",
    type: "select",
    text: "Timezone",
    id: "timeZone",
    value: scheduleObj?.timezoneName || "",
    change: selectedTimezoneCbk,
    options: userDetail?.globalTimeZone,
    timeZone: true,
    search: true,
    displayName: "displayName",
    tabIndex: -1
  };

  return (
    <div className="schedulesWrapper">
      <div className="schedulesCont">
        <div className="createScheduleWrapper">
          <div className="fieldContainer">
            <div className="fieldWrapper">
              <NameField
                scheduleObj={scheduleObj}
                scheduleNameErr={scheduleNameErr}
                scheduleNameChange={scheduleNameChange}
              />
            </div>
            <div className="fieldWrapper timeZoneWrapper">
              <TimezoneSelectDropdown {...timeZoneObj} />
            </div>
          </div>
          <div className="fieldWrapper sendingWindowWrapper">
            <label className="labelText">
              Sending window <span className="required">*</span>
            </label>
            <div className="quickWindowSelect">
              <span className="quickWindowLabel">Quick select:</span>
              {QUICK_WINDOWS.map((preset = {}) => (
                <button
                  type="button"
                  key={preset?.id}
                  className={`quickWindowChip ${
                    isQuickWindowActive(preset) ? "active" : ""
                  }`}
                  onClick={() => handleQuickWindowClick(preset)}
                >
                  <i className="material-icons-outlined">schedule</i>
                  {preset?.name}
                </button>
              ))}
            </div>
            <SendingWindow
              scheduleSendingWindowCbk={scheduleSendingWindowCbk}
              dailyTimeRange={scheduleObj.timerange}
            />
          </div>
          <FormActionButton
            cancel={handleCancelAction}
            save={handleSaveChangesAction}
            saveBtnText="Save changes"
            loader={loader}
            className={`saveSchedule ${loader ? "loading" : ""}`}
          />
        </div>
      </div>
    </div>
  );
});

export { CreateOrEditSchedules };
export default CreateOrEditSchedules;
